
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, isToday, isYesterday, startOfDay } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { EmptyChats } from "@/components/saved-chats/EmptyChats";
import { ChatDateGroup } from "@/components/saved-chats/ChatDateGroup";
import { ChatFilters } from "@/components/saved-chats/ChatFilters";
import { getTranslations } from "@/utils/translations";
import { useToast } from "@/hooks/use-toast";

interface SavedChat {
  id: string;
  title: string;
  content: string;
  created_at: string;
  is_favorite: boolean;
  is_processed?: boolean;
  updated_at: string;
  user_id?: string;
}

const SavedChats = () => {
  const preferredLanguage = localStorage.getItem('preferred_language') || 'es';
  const t = getTranslations(preferredLanguage as 'en' | 'es');
  const { toast } = useToast();
  
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("todas");
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const { data: chats, isLoading, refetch } = useQuery({
    queryKey: ["saved-chats"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_chats")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as SavedChat[];
    },
  });

  // Filter chats based on search query, category, and favorites
  const filteredChats = chats?.filter(chat => {
    const chatContent = JSON.parse(chat.content);
    const matchesSearch = searchQuery === "" || 
      chatContent.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      chatContent.answer.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFavorites = !showFavoritesOnly || chat.is_favorite;
    
    // TODO: Implement category filtering when categories are added to the database
    const matchesCategory = selectedCategory === "todas";
    
    return matchesSearch && matchesFavorites && matchesCategory;
  }) || [];

  // Group chats by date
  const groupedChats = filteredChats.reduce((groups, chat) => {
    const chatDate = startOfDay(new Date(chat.created_at)).toISOString();
    if (!groups[chatDate]) {
      groups[chatDate] = [];
    }
    groups[chatDate].push(chat);
    return groups;
  }, {} as Record<string, SavedChat[]>);

  // Sort dates (most recent first)
  const sortedDates = Object.keys(groupedChats).sort((a, b) => 
    new Date(b).getTime() - new Date(a).getTime()
  );

  const handleToggleFavorite = async (chatId: string) => {
    try {
      const chat = chats?.find(c => c.id === chatId);
      if (!chat) return;

      const { error } = await supabase
        .from("saved_chats")
        .update({ is_favorite: !chat.is_favorite })
        .eq("id", chatId);

      if (error) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "No se pudo actualizar el favorito"
        });
        return;
      }

      toast({
        title: chat.is_favorite ? "Removido de favoritos" : "Agregado a favoritos",
        description: chat.is_favorite 
          ? "La conversación se removió de favoritos" 
          : "La conversación se agregó a favoritos"
      });

      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Ocurrió un error inesperado"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
        {/* Clean Header */}
        <div className="bg-white py-16 md:py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
              Mis Conversaciones
            </h1>
            <p className="text-lg text-coolGray max-w-2xl mx-auto">
              Aquí puedes ver y gestionar el historial de tus preguntas.
            </p>
          </div>
        </div>
        
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto" style={{ gap: '1.5rem', display: 'flex', flexDirection: 'column' }}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div 
                  className="h-[120px] bg-white rounded-xl border border-gray-100"
                  style={{ 
                    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
                    padding: '1.25rem'
                  }}
                >
                  <div className="space-y-3">
                    <div className="h-5 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-4 bg-gray-100 rounded w-1/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      {/* Clean Header Section */}
      <div className="bg-white py-16 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
            Mis Conversaciones
          </h1>
          <p className="text-lg text-coolGray max-w-2xl mx-auto">
            Organiza y encuentra fácilmente el historial de tus consultas.
          </p>
        </div>
      </div>
      
      {/* Chat List Section */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          {chats?.length === 0 ? (
            <EmptyChats t={t} />
          ) : (
            <>
              {/* Filters and Search */}
              <ChatFilters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                showFavoritesOnly={showFavoritesOnly}
                onToggleFavorites={() => setShowFavoritesOnly(!showFavoritesOnly)}
              />

              {/* Results Summary */}
              {(searchQuery || showFavoritesOnly) && (
                <div className="mb-4 text-sm text-coolGray">
                  {filteredChats.length === 0 ? (
                    "No se encontraron conversaciones con los filtros aplicados."
                  ) : (
                    `Mostrando ${filteredChats.length} de ${chats?.length || 0} conversaciones`
                  )}
                </div>
              )}

              {/* Grouped Chats by Date */}
              {sortedDates.length > 0 ? (
                <div className="space-y-6">
                  {sortedDates.map((dateKey) => {
                    const dateChats = groupedChats[dateKey];
                    const date = new Date(dateKey);
                    
                    return (
                      <ChatDateGroup
                        key={dateKey}
                        date={dateKey}
                        chats={dateChats}
                        t={t}
                        onToggleFavorite={handleToggleFavorite}
                        isToday={isToday(date)}
                        isYesterday={isYesterday(date)}
                      />
                    );
                  })}
                </div>
              ) : (
                /* No Results */
                <div className="text-center py-12">
                  <div className="text-coolGray mb-4">
                    No se encontraron conversaciones
                  </div>
                  <button
                    onClick={() => {
                      setSearchQuery("");
                      setSelectedCategory("todas");
                      setShowFavoritesOnly(false);
                    }}
                    className="text-primary hover:text-primary/80 underline"
                  >
                    Limpiar filtros
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavedChats;
